<template>
  <v-dialog
    v-model="isOpen"
    max-width="560"
    scrollable
    aria-label="Plan my week"
  >
    <v-card>
      <v-card-title>Plan my week</v-card-title>
      <v-card-text v-if="!proposal">
        <p class="mb-4">
          Picks meals from your own recipes for the empty slots. You check them
          before anything is added.
        </p>
        <fieldset class="choice mb-4">
          <legend class="text-title-small mb-2">Days</legend>
          <v-chip-group v-model="days" multiple column>
            <v-chip
              v-for="day in orderedDays"
              :key="day"
              :value="day"
              filter
              variant="outlined"
              class="text-capitalize"
            >
              {{ day }}
            </v-chip>
          </v-chip-group>
        </fieldset>
        <fieldset class="choice mb-2">
          <legend class="text-title-small mb-2">Meals</legend>
          <v-chip-group v-model="slots" multiple column>
            <v-chip
              v-for="slot in SLOTS"
              :key="slot"
              :value="slot"
              filter
              variant="outlined"
            >
              {{ capitalize(slot) }}
            </v-chip>
          </v-chip-group>
        </fieldset>
        <v-textarea
          v-model="wish"
          label="Anything to keep in mind? (optional)"
          placeholder="Quick on weekdays, no fish"
          :counter="MAX_WISH_LENGTH"
          :maxlength="MAX_WISH_LENGTH"
          rows="2"
          auto-grow
          variant="outlined"
          class="mt-2"
        />
        <p class="text-body-medium" aria-live="polite">
          {{ toPlanLabel }}
        </p>
        <v-alert v-if="error" type="error" variant="tonal" class="mt-4">
          {{ error }}
        </v-alert>
      </v-card-text>
      <v-card-text v-else>
        <p v-if="!rows.length">
          No meals were picked. Try again, or choose other days or meals.
        </p>
        <template v-else>
          <p class="mb-4">
            Change a meal or leave it out, then add them to your week.
          </p>
          <div
            v-for="row in rows"
            :key="`${row.day}-${row.slot}`"
            class="d-flex align-center ga-2"
          >
            <v-select
              :model-value="row.meal.recipeId"
              :items="recipeItems(row.meal)"
              :label="`${capitalize(row.day)} ${row.slot}`"
              variant="outlined"
              density="comfortable"
              @update:model-value="
                (recipeId: string) => swap(row.day, row.slot, recipeId)
              "
            />
            <v-btn
              icon="mdi-close"
              variant="text"
              class="mb-5"
              :aria-label="`Leave out ${row.day} ${row.slot}`"
              @click="leaveOut(row.day, row.slot)"
            />
          </div>
        </template>
        <p v-if="missing" class="text-body-medium">
          No match for {{ missing }} {{ missing === 1 ? 'meal' : 'meals' }}: add
          {{ missing === 1 ? 'it' : 'them' }} yourself, or try again.
        </p>
        <v-alert v-if="error" type="error" variant="tonal" class="mt-4">
          {{ error }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <template v-if="!proposal">
          <v-spacer />
          <v-btn variant="text" @click="isOpen = false">Cancel</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            prepend-icon="mdi-creation"
            :disabled="!toPlan"
            :loading="planning"
            @click="requestPlan"
          >
            Plan meals
            <!-- Vuetify's own spinner has no accessible name -->
            <template v-slot:loader>
              <v-progress-circular
                indeterminate
                size="20"
                width="2"
                aria-label="Planning meals"
              />
            </template>
          </v-btn>
        </template>
        <template v-else>
          <v-btn variant="text" @click="$emit('reset')">Back</v-btn>
          <v-spacer />
          <v-btn variant="text" :loading="planning" @click="requestPlan">
            Try again
            <!-- Vuetify's own spinner has no accessible name -->
            <template v-slot:loader>
              <v-progress-circular
                indeterminate
                size="20"
                width="2"
                aria-label="Planning meals"
              />
            </template>
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :disabled="!rows.length"
            @click="accept"
          >
            Add {{ rows.length }} {{ rows.length === 1 ? 'meal' : 'meals' }}
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
  import { computed, defineComponent, type PropType, ref, watch } from 'vue';
  import {
    type Day,
    type MealEntry,
    SLOTS,
    type Slot,
    type WeekPlan,
  } from '@/service/weekplan/types';
  import type { Recipe } from '@/service/recipe/types';
  import {
    emptySlots,
    MAX_WISH_LENGTH,
    type PlanRequest,
    type Proposal,
    type ProposedMeals,
    slotCount,
  } from '@/service/weekplan/weekPlanner';

  function capitalize(text: string): string {
    return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
  }

  export default defineComponent({
    name: 'PlanWeekDialog',
    props: {
      open: {
        type: Boolean,
        default: false,
      },
      plan: {
        type: Object as PropType<WeekPlan>,
        required: true,
      },
      // The days in the order the week shows them
      orderedDays: {
        type: Array as PropType<Day[]>,
        required: true,
      },
      recipes: {
        type: Array as PropType<Recipe[]>,
        default: () => [],
      },
      planning: {
        type: Boolean,
        default: false,
      },
      // Shown for checking once set
      proposal: {
        type: Object as PropType<Proposal>,
        default: undefined,
      },
      error: {
        type: String,
        default: '',
      },
    },
    emits: {
      plan: (_request: PlanRequest) => true,
      accept: (_meals: ProposedMeals) => true,
      reset: () => true,
      close: () => true,
    },
    setup(props, { emit }) {
      const isOpen = ref(props.open);
      const days = ref<Day[]>([...props.orderedDays]);
      const slots = ref<Slot[]>(['dinner']);
      const wish = ref('');
      const meals = ref<ProposedMeals>({});

      watch(
        () => props.open,
        (open) => {
          isOpen.value = open;
        },
      );
      watch(isOpen, (open) => {
        if (!open) emit('close');
      });
      watch(
        () => props.proposal,
        (proposal) => {
          meals.value = JSON.parse(JSON.stringify(proposal?.meals ?? {}));
        },
        { immediate: true },
      );

      const requested = computed(() =>
        emptySlots(props.plan, days.value, slots.value),
      );
      const toPlan = computed(() => slotCount(requested.value));

      const rows = computed(() =>
        props.orderedDays.flatMap((day) =>
          SLOTS.filter((slot) => meals.value[day]?.[slot]).map((slot) => ({
            day,
            slot,
            meal: meals.value[day]![slot]!,
          })),
        ),
      );

      return {
        SLOTS,
        MAX_WISH_LENGTH,
        capitalize,
        isOpen,
        days,
        slots,
        wish,
        toPlan,
        toPlanLabel: computed(() =>
          toPlan.value
            ? `${toPlan.value} empty ${toPlan.value === 1 ? 'meal' : 'meals'} to plan`
            : 'These meals are already planned.',
        ),
        rows,
        missing: computed(() =>
          Math.max(0, toPlan.value - (props.proposal?.count ?? 0)),
        ),
        // The user's recipes; a proposed one that is not among them (yet) is
        // offered too, so the field shows its title
        recipeItems(meal: MealEntry) {
          const items = props.recipes.map(({ id, title }) => ({
            title,
            value: id,
          }));
          return items.some(({ value }) => value === meal.recipeId)
            ? items
            : [{ title: meal.title, value: meal.recipeId }, ...items];
        },
        requestPlan() {
          emit('plan', {
            weekStart: props.plan.weekStart,
            slots: requested.value,
            wish: wish.value.trim(),
          });
        },
        swap(day: Day, slot: Slot, recipeId: string) {
          const recipe = props.recipes.find(({ id }) => id === recipeId);
          if (!recipe) return;
          meals.value = {
            ...meals.value,
            [day]: {
              ...meals.value[day],
              [slot]: { recipeId: recipe.id, title: recipe.title },
            },
          };
        },
        leaveOut(day: Day, slot: Slot) {
          const { [slot]: _, ...rest } = meals.value[day] ?? {};
          meals.value = { ...meals.value, [day]: rest };
        },
        accept() {
          emit('accept', meals.value);
        },
      };
    },
  });
</script>

<style scoped>
  .choice {
    border: 0;
    padding: 0;
    margin-inline: 0;
  }
</style>
